import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  Typography,
} from '@mui/material';
import {
  AttachMoney,
  EuroSymbol,
  Block,
} from '@mui/icons-material';

/**
 * Reusable Payment Status Dialog Component
 * Used for marking invoices/purchase orders as Paid/Unpaid
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Function to close dialog
 * @param {Function} props.onSubmit - Function to handle submission
 * @param {boolean} props.isMarkingAsPaid - true for marking as paid, false for unpaid
 * @param {Object} props.document - Document object with details (documentNo, sender/supplierName, paidDate)
 * @param {string} props.documentType - Type of document ('invoice' or 'purchase-order')
 */
const PaymentStatusDialog = ({
  open,
  onClose,
  onSubmit,
  isMarkingAsPaid,
  document,
  documentType = 'invoice'
}) => {
  const [paymentForm, setPaymentForm] = useState({
    paidDate: new Date().toISOString().split('T')[0],
    paymentSource: '',
    paymentNotes: ''
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const handleSubmit = () => {
    setSubmitAttempted(true);

    if (isMarkingAsPaid) {
      if (!paymentForm.paidDate || !paymentForm.paymentSource) {
        return;
      }
    }

    onSubmit(paymentForm);
    handleClose();
  };

  const handleClose = () => {
    setPaymentForm({
      paidDate: new Date().toISOString().split('T')[0],
      paymentSource: '',
      paymentNotes: ''
    });
    setSubmitAttempted(false);
    onClose();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: isMarkingAsPaid
            ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
            : 'linear-gradient(135deg, #f44336 0%, #e57373 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2.5,
        }}
      >
        {isMarkingAsPaid ? (
          <AttachMoney sx={{ fontSize: 32 }} />
        ) : (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <EuroSymbol sx={{ fontSize: 32 }} />
            <Block sx={{ fontSize: 32, position: 'absolute', color: '#fff' }} />
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isMarkingAsPaid ? 'Mark Invoice as Paid' : 'Mark Invoice as Not Paid'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {/* Document summary */}
        {document && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid rgba(102, 126, 234, 0.2)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Document No:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {document.documentNo || document.PurchaseInvoiceNumber || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {documentType === 'purchase-order' ? 'Supplier:' : 'Sender:'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>
                {document.sender || document.SupplierName || 'N/A'}
              </Typography>
            </Box>
            {document.paidDate && !isMarkingAsPaid && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Paid On:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDate(document.paidDate)}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
        
        {isMarkingAsPaid ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Payment Date *"
              type="date"
              value={paymentForm.paidDate}
              onChange={(e) => setPaymentForm({ ...paymentForm, paidDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              error={submitAttempted && !paymentForm.paidDate}
              helperText={submitAttempted && !paymentForm.paidDate ? 'Payment date is required' : ''}
            />
            <FormControl fullWidth required error={submitAttempted && !paymentForm.paymentSource}>
              <InputLabel>Payment Method *</InputLabel>
              <Select
                value={paymentForm.paymentSource}
                onChange={(e) => setPaymentForm({ ...paymentForm, paymentSource: e.target.value })}
                label="Payment Method *"
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Bancontact">Bancontact</MenuItem>
                <MenuItem value="Virement">Virement</MenuItem>
              </Select>
              {submitAttempted && !paymentForm.paymentSource && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  Payment method is required
                </Typography>
              )}
            </FormControl>
            <TextField
              label="Payment Notes"
              value={paymentForm.paymentNotes}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentNotes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              placeholder="Optional notes about this payment..."
            />
          </Box>
        ) : (
          <DialogContentText sx={{ mt: 1, color: 'text.primary', fontSize: '1rem' }}>
            Are you sure you want to mark this {documentType === 'purchase-order' ? 'purchase order' : 'invoice'} as not paid? 
            This will clear all payment details.
          </DialogContentText>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: 'none', px: 3, borderRadius: 2, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isMarkingAsPaid ? 'success' : 'error'}
          startIcon={
            isMarkingAsPaid ? (
              <AttachMoney />
            ) : (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <EuroSymbol />
                <Block sx={{ position: 'absolute', fontSize: '1.2em' }} />
              </Box>
            )
          }
          sx={{ textTransform: 'none', px: 3, borderRadius: 2, fontWeight: 600 }}
        >
          {isMarkingAsPaid ? 'Confirm Payment' : 'Mark as Not Paid'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentStatusDialog;
